import { useCallback, useEffect, useRef, useState } from "react";
import { stableStringify } from "./summary-staleness";

/**
 * Debounced auto-persist for section-local editing state (#320).
 *
 * Built for GearListSection's quote details, which used to live only in React
 * state until "Generate Quote Summary" saved them — navigating away first
 * silently dropped every edit. This hook persists the latest snapshot:
 *
 *   - debounced: each change while `enabled` re-arms a `delayMs` timer, so a
 *     burst of keystrokes collapses into one save of the final value
 *   - gated: when `enabled` is false (the caller's validation fails) nothing
 *     is persisted — half-typed rows never reach the server
 *   - flushed: a pending unsaved change is fired on unmount, so SPA
 *     navigation away from the workspace can't drop edits
 *   - serialised: saves are chained on an internal promise queue so an
 *     in-flight save can't be overtaken by a newer one (the PUT endpoints are
 *     full-replace, so out-of-order completion would resurrect stale data)
 *
 * Change detection fingerprints `value` via stableStringify against the last
 * successfully-saved snapshot (the mount-time value is the baseline — it is
 * what the server already has). A failed save keeps the old fingerprint, so
 * the next change or the unmount flush retries it.
 *
 * `saveNow()` bypasses the debounce AND the change check: it cancels any
 * pending timer and always persists, returning the save promise (rejections
 * propagate to the caller). Action buttons that must save-then-act (Generate
 * Quote Summary) call this so their behaviour stays save-first.
 */
export interface UseAutosaveOptions<T> {
  /** Latest snapshot of the persistable state. */
  value: T;
  /** Gate — when false, no auto-save is armed and no unmount flush fires. */
  enabled: boolean;
  /** Persist the snapshot. */
  save: (value: T) => Promise<void>;
  /** Debounce window in ms. */
  delayMs?: number;
}

export interface AutosaveHandle {
  /**
   * Cancel any pending debounce and persist the current value immediately,
   * even if it matches the last-saved snapshot. Returns the save promise.
   */
  saveNow: () => Promise<void>;
  /**
   * True while `value` differs from the last successfully-saved snapshot.
   * Because `save` typically awaits the post-save refetch (onRefresh), the
   * flag spans the whole save round-trip — it is the section-dirty operand
   * of the staleness OR (summary-staleness.ts).
   */
  dirty: boolean;
}

export function useAutosave<T>({
  value,
  enabled,
  save,
  delayMs = 1000,
}: UseAutosaveOptions<T>): AutosaveHandle {
  const print = stableStringify(value);

  // Mirror the freshest value/print/flags into refs so the timer callback,
  // the unmount flush, and saveNow always operate on the latest snapshot.
  const valueRef = useRef(value);
  valueRef.current = value;
  const printRef = useRef(print);
  printRef.current = print;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const saveRef = useRef(save);
  saveRef.current = save;

  // Fingerprint of the last snapshot known to be persisted. The mount-time
  // value is the baseline — it came from the server. Held in state as well as
  // a ref: the ref is what the async callbacks read; the state makes the
  // returned `dirty` flag reactive when a save completes.
  const [savedPrint, setSavedPrint] = useState(print);
  const savedPrintRef = useRef(print);
  // Promise queue serialising saves (full-replace endpoints must not race).
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enqueueSave = useCallback((force: boolean): Promise<void> => {
    const run = queueRef.current.then(async () => {
      // Re-read at execution time: a queued save persists the freshest
      // snapshot, and a no-op (already saved while queued) is skipped. The
      // gate is re-checked too — a save queued behind a slow request must
      // not persist a snapshot that has since gone invalid.
      const latestPrint = printRef.current;
      if (
        !force &&
        (!enabledRef.current || latestPrint === savedPrintRef.current)
      )
        return;
      await saveRef.current(valueRef.current);
      savedPrintRef.current = latestPrint;
      setSavedPrint(latestPrint);
    });
    // Keep the queue alive after a rejection; the caller still sees the
    // rejection through the returned promise.
    queueRef.current = run.catch(() => {});
    return run;
  }, []);

  // Debounced auto-save: any change while enabled (re-)arms the timer.
  useEffect(() => {
    if (!enabled) return;
    if (print === savedPrintRef.current) return;
    const timer = setTimeout(() => {
      timerRef.current = null;
      enqueueSave(false).catch((err) => {
        // Soft-fail: the fingerprint stays un-advanced, so the next edit or
        // the unmount flush retries. Generate still saves explicitly.
        console.error("Auto-save failed:", err);
      });
    }, delayMs);
    timerRef.current = timer;
    return () => clearTimeout(timer);
  }, [print, enabled, delayMs, enqueueSave]);

  // Flush a pending unsaved change on unmount (SPA navigation away).
  useEffect(() => {
    return () => {
      if (!enabledRef.current) return;
      if (printRef.current === savedPrintRef.current) return;
      enqueueSave(false).catch((err) => {
        console.error("Auto-save flush failed:", err);
      });
    };
  }, [enqueueSave]);

  const saveNow = useCallback((): Promise<void> => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return enqueueSave(true);
  }, [enqueueSave]);

  return { saveNow, dirty: print !== savedPrint };
}
